/**
 * Client for GET /api/cognitory/v2/platform-admin/systems.
 * Platform-administrator inventory only; not membership-scoped access.
 */

import {
  COGNITORY_V2_PLATFORM_ADMIN_SYSTEMS_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";

export type PlatformAdministratorSystemEntry = {
  systemId: string;
  displayName: string;
  status: string;
  enterable: boolean;
  canOpen: boolean;
  canSetup: boolean;
  initialOwnerAssignmentStatus: "unassigned" | "assigned" | "unavailable";
};

export type PlatformAdministratorSystemsClientResult =
  | {
      kind: "listed";
      requestId: string;
      systems: PlatformAdministratorSystemEntry[];
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "temporary_failure"
        | "unable_to_verify";
      requestId: string | null;
    };

function isEntry(value: unknown): value is PlatformAdministratorSystemEntry {
  if (!value || typeof value !== "object") return false;
  const record = value as Record<string, unknown>;
  return (
    typeof record.systemId === "string" &&
    record.systemId.length > 0 &&
    typeof record.displayName === "string" &&
    record.displayName.length > 0 &&
    typeof record.status === "string" &&
    record.status.length > 0 &&
    typeof record.enterable === "boolean" &&
    typeof record.canOpen === "boolean" &&
    typeof record.canSetup === "boolean" &&
    (record.initialOwnerAssignmentStatus === "unassigned" ||
      record.initialOwnerAssignmentStatus === "assigned" ||
      record.initialOwnerAssignmentStatus === "unavailable")
  );
}

export async function fetchPlatformAdministratorSystems(input?: {
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<PlatformAdministratorSystemsClientResult> {
  const fetchImpl = input?.fetchImpl ?? fetch;
  const token =
    input?.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const base = input?.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${COGNITORY_V2_PLATFORM_ADMIN_SYSTEMS_PATH}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        cache: "no-store",
      },
    );
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId =
    body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
      ? (body as { requestId: string }).requestId
      : null;

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }
  if (response.status !== 200 || !body || typeof body !== "object") {
    return { kind: "unable_to_verify", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision !== "systems_listed") {
    return { kind: "unable_to_verify", requestId };
  }

  const rawList = (body as { systems?: unknown }).systems;
  if (!Array.isArray(rawList)) {
    return { kind: "unable_to_verify", requestId };
  }

  const systems: PlatformAdministratorSystemEntry[] = [];
  for (const item of rawList) {
    if (isEntry(item)) {
      systems.push({
        systemId: item.systemId,
        displayName: item.displayName,
        status: item.status,
        enterable: item.enterable,
        canOpen: item.canOpen,
        canSetup: item.canSetup,
        initialOwnerAssignmentStatus: item.initialOwnerAssignmentStatus,
      });
    }
  }

  return {
    kind: "listed",
    requestId: requestId ?? "unknown",
    systems,
  };
}
